import * as Sentry from "@sentry/react";
import { ComponentType, lazy, LazyExoticComponent } from "react";

/*
 * public: anyone. guest: logged out only, logged in users go to their
 * dashboard. client, coach, user (either): logged in as that type.
 */
export type PageAccess = "public" | "guest" | "client" | "coach" | "user";

type PageModule = { default: ComponentType };

type PageDefinition = {
    access: PageAccess;
    load: () => Promise<PageModule>;
    // The page reads ?redirect: where to send the user once they are done (logging in, verifying).
    acceptsRedirect?: true;
    // The mobile app shows this page to logged out users; every other page sends them to log in, as RedirectMobileApp does.
    shownToLoggedOutMobileApp?: true;
};

/*
 * Every page the SPA renders, keyed by its Laravel route name. The URL comes
 * from that route's definition in Ziggy, so routes/pages.php stays the only
 * place a page's URL is written down.
 */
export const pageDefinitions = {
    home: {
        access: "guest",
        load: () => import("@/Pages/Home/CareerPlusHome"),
    },
    quiz: { access: "guest", load: () => import("@/Pages/Quiz") },
    start: { access: "guest", load: () => import("@/Pages/Start") },
    "client.register": {
        access: "guest",
        load: () =>
            import("@/Pages/Auth/ClientRegister/ClientRegisterWithGoogle"),
    },
    "coach-apply": {
        access: "guest",
        load: () => import("@/Pages/CoachApply/CoachApply"),
    },
    faq: { access: "public", load: () => import("@/Pages/FAQ") },
    contact: { access: "public", load: () => import("@/Pages/ContactUs") },
    privacy: {
        access: "public",
        load: () => import("@/Pages/Legalese/PrivacyPolicy"),
    },
    terms: { access: "public", load: () => import("@/Pages/Legalese/TOS") },
    about: { access: "public", load: () => import("@/Pages/About") },
    reviews: {
        access: "public",
        load: () => import("@/Pages/Reviews/Reviews"),
    },
    archetypes: { access: "public", load: () => import("@/Pages/Archetypes") },
    error: { access: "public", load: () => import("@/Pages/Error") },
    "client.login": {
        access: "guest",
        load: () => import("@/Pages/Auth/ClientLogin/ClientLoginPage"),
        acceptsRedirect: true,
        shownToLoggedOutMobileApp: true,
    },
    "coach.login": {
        access: "guest",
        load: () => import("@/Pages/Auth/CoachLogin"),
        acceptsRedirect: true,
        shownToLoggedOutMobileApp: true,
    },
    "coach.register": {
        access: "guest",
        load: () => import("@/Pages/Auth/Register"),
    },
    "coach.password.request": {
        access: "guest",
        load: () => import("@/Pages/Auth/ForgotPassword"),
        shownToLoggedOutMobileApp: true,
    },
    "coach.password.reset": {
        access: "guest",
        load: () => import("@/Pages/Auth/ResetPassword"),
        shownToLoggedOutMobileApp: true,
    },
    "password.confirm": {
        access: "coach",
        load: () => import("@/Pages/Auth/ConfirmPassword"),
    },
    "two-factor.index": {
        access: "coach",
        load: () => import("@/Pages/Auth/TwoFactor"),
    },
    "two-factor.setup": {
        access: "coach",
        load: () => import("@/Pages/Auth/TwoFactorSetup/TwoFactorSetup"),
        shownToLoggedOutMobileApp: true,
    },
    "verification.notice": {
        access: "public",
        load: () => import("@/Pages/Auth/VerifyEmailNotice"),
        acceptsRedirect: true,
    },
    "client.register.phone-number": {
        access: "client",
        load: () =>
            import("@/Pages/Auth/ClientRegister/ClientRegisterPhoneNumber"),
    },
    "client.register.verify-phone-page": {
        access: "client",
        load: () =>
            import("@/Pages/Auth/ClientRegister/ClientPhoneVerification"),
    },
    "signup-code": {
        access: "guest",
        load: () => import("@/Pages/SignupCode"),
    },
    "say-more": {
        access: "client",
        load: () => import("@/Pages/Client/SayMore/SayMore"),
    },
    "client.intakeQuizSummary": {
        access: "client",
        load: () => import("@/Pages/Client/IntakeQuizSummary"),
    },
    "personalized-intake-quiz.index": {
        access: "client",
        load: () =>
            import(
                "@/Pages/Client/PersonalizedIntakeQuiz/PersonalizedIntakeQuiz"
            ),
    },
    "coach.clients": {
        access: "coach",
        load: () => import("@/Pages/Coach/ClientList/ClientList"),
    },
    "client.dashboard": {
        access: "client",
        load: () => import("@/Pages/Client/Dashboard"),
    },
    "coach.dashboard": {
        access: "coach",
        load: () => import("@/Pages/Coach/Dashboard"),
        shownToLoggedOutMobileApp: true,
    },
    "coach.availability": {
        access: "coach",
        load: () => import("@/Pages/Coach/Availability"),
    },
    "coach.coach-terms": {
        access: "public",
        load: () => import("@/Pages/Legalese/CoachTerms"),
    },
    "quiz-storyboard": {
        access: "public",
        load: () => import("@/Pages/Employee/QuizStoryboard/QuizStoryboard"),
    },
    "exercise-builder": {
        access: "public",
        load: () => import("@/Pages/Employee/ExerciseBuilder"),
    },
    "client.feedback360.dashboard": {
        access: "client",
        load: () => import("@/Pages/Client/360Dashboard"),
    },
    "feedback360.rater.landing": {
        access: "public",
        load: () => import("@/Pages/Feedback360/Public/Landing"),
    },
    "feedback360.rater.questionnaire": {
        access: "public",
        load: () => import("@/Pages/Feedback360/Public/Questionnaire"),
    },
    "appointmentReview.create": {
        access: "client",
        load: () => import("@/Pages/Client/RateSession"),
    },
    "scheduling-reminder-notification": {
        access: "client",
        load: () => import("@/Pages/Client/SchedulingNotificationReminder"),
    },
    "client.contact-us.show": {
        access: "client",
        load: () => import("@/Pages/HowCanWeHelp/HowCanWeHelp"),
    },
    "client-exercise.summary": {
        access: "client",
        load: () => import("@/Pages/ExerciseSummary"),
    },
    "client-exercises.show": {
        access: "user",
        load: () => import("@/Pages/Client/ExercisePage"),
    },
    "exercises.show": {
        access: "coach",
        load: () => import("@/Pages/Client/ExercisePage"),
    },
    "email-unsubscribe": {
        access: "public",
        load: () => import("@/Pages/EmailUnsubscribe"),
    },
    "notifications.opt-out": {
        access: "public",
        load: () => import("@/Pages/NotificationOptOut"),
    },
    "phone.unsubscribe": {
        access: "public",
        load: () => import("@/Pages/PhoneUnsubscribe"),
    },
    "phone.resubscribe": {
        access: "public",
        load: () => import("@/Pages/PhoneUnsubscribe"),
    },
    "client.sessionRecording.show": {
        access: "client",
        load: () => import("@/Pages/Client/SessionRecording/Show"),
    },
    earnings: {
        access: "coach",
        load: () => import("@/Pages/Coach/Earnings"),
    },
    "coach.earnings-breakdown": {
        access: "coach",
        load: () => import("@/Pages/Coach/Payments/Index"),
    },
    "coach.payment-setup": {
        access: "coach",
        load: () => import("@/Pages/Coach/Payments/PaymentSetup"),
    },
    "coach.payments-history": {
        access: "coach",
        load: () => import("@/Pages/Coach/Payments/PayoutHistory"),
    },
    cap: {
        access: "coach",
        load: () => import("@/Pages/Coach/Cap"),
    },
    "coach.potentialClients": {
        access: "coach",
        load: () =>
            import("@/Pages/Coach/PotentialClientList/PotentialClientList"),
    },
    "coach.reportNoShow": {
        access: "coach",
        load: () => import("@/Pages/Coach/CoachReportNoShow"),
    },
    "videoSessionsHistory.index": {
        access: "coach",
        load: () => import("@/Pages/Coach/VideoSessionsHistory/Index"),
    },
    "videoSessionsHistory.show": {
        access: "coach",
        load: () => import("@/Pages/Coach/VideoSessionsHistory/Show"),
    },
    "coach.feedback360.showExample": {
        access: "coach",
        load: () => import("@/Pages/Coach/360DashboardExample"),
    },
    "coach.feedback360.show": {
        access: "coach",
        load: () => import("@/Pages/Coach/360Dashboard"),
    },
    "coach.acceptingNewClientsPage": {
        access: "coach",
        load: () => import("@/Pages/Coach/AcceptingNewClientsPage"),
    },
    "exercises.index": {
        access: "coach",
        load: () => import("@/Pages/Coach/ExerciseLibraryPage"),
    },
    trainings: {
        access: "coach",
        load: () => import("@/Pages/Coach/Trainings"),
    },
    "coach-intake.personal-information.index": {
        access: "coach",
        load: () => import("@/Pages/CoachIntake/PersonalInformation"),
    },
    "coach-intake.contractor-agreement.index": {
        access: "coach",
        load: () => import("@/Pages/CoachIntake/ContractorAgreement"),
    },
    "coach-intake.coach-experience.index": {
        access: "coach",
        load: () => import("@/Pages/CoachIntake/CoachExperience"),
    },
    "coach-intake.preference-and-style.index": {
        access: "coach",
        load: () =>
            import("@/Pages/CoachIntake/PreferenceAndStyle/PreferenceAndStyle"),
    },
    "coach-intake.professional-background.index": {
        access: "coach",
        load: () =>
            import(
                "@/Pages/CoachIntake/ProfessionalBackground/ProfessionalBackground"
            ),
    },
    "coach-intake.career-coaching-experience.index": {
        access: "coach",
        load: () => import("@/Pages/CoachIntake/CareerCoachingExperience"),
    },
    "coach-intake.personal-details.index": {
        access: "coach",
        load: () => import("@/Pages/CoachIntake/PersonalDetails"),
    },
    "coach-intake.scope-of-practice.index": {
        access: "coach",
        load: () => import("@/Pages/CoachIntake/ScopeOfPractice"),
    },
    "coach-intake.next-steps.index": {
        access: "coach",
        load: () => import("@/Pages/CoachIntake/NextSteps"),
    },
    "coach-intake.learning-videos.index": {
        access: "coach",
        load: () => import("@/Pages/CoachIntake/LearningVideos"),
    },
    "coach-intake.orientation.index": {
        access: "coach",
        load: () => import("@/Pages/CoachIntake/Orientation"),
    },
    "coach-intake.testimonials.index": {
        access: "coach",
        load: () => import("@/Pages/CoachIntake/Testimonials"),
    },
    chat: { access: "user", load: () => import("@/Pages/Chat/Chat") },
    "user-details": {
        access: "user",
        load: () => import("@/Pages/UserDetails"),
    },
    "board.get": { access: "user", load: () => import("@/Pages/BoardPage") },
    "video-test": { access: "user", load: () => import("@/Pages/VideoTest") },
    "client.my-sessions": {
        access: "client",
        load: () => import("@/Pages/Client/MySessionsClientPage"),
    },
    "client.past-sessions": {
        access: "client",
        load: () => import("@/Pages/Client/PastSessionsPage"),
    },
    "client.upcoming-topics": {
        access: "client",
        load: () => import("@/Pages/Client/UpcomingTopicsClientPage"),
    },
    "client.action-items.index": {
        access: "client",
        load: () => import("@/Pages/Client/ActionItems"),
    },
    "client.exercises.index": {
        access: "client",
        load: () => import("@/Pages/Client/ClientExercises"),
    },
    "client.profile.edit": {
        access: "client",
        load: () => import("@/Pages/Profile/Profile"),
    },
    "coach.profile.edit": {
        access: "coach",
        load: () => import("@/Pages/Profile/Profile"),
    },
    "subscription.price": {
        access: "client",
        load: () => import("@/Pages/Client/ManagePlan"),
    },
    "quit-page.index": {
        access: "client",
        load: () => import("@/Pages/Client/QuitPage"),
    },
    "did-quit": {
        access: "client",
        load: () => import("@/Pages/Client/DidQuit"),
    },
    "financial-assistance.offer-page": {
        access: "client",
        load: () => import("@/Pages/Client/FinancialAssistance/OfferPage"),
    },
    "switch-page.index": {
        access: "client",
        load: () => import("@/Pages/Client/SwitchPage"),
    },
    "switch-preferences.show": {
        access: "client",
        load: () => import("@/Pages/Client/SwitchPreferences"),
    },
    "client.paymentMethod.view": {
        access: "client",
        load: () => import("@/Pages/Client/PaymentPage/PaymentPage"),
    },
    "second-bite.view-coaches": {
        access: "client",
        load: () => import("@/Pages/Client/SecondBite/SelectCoach"),
    },
    "second-bite.payment": {
        access: "client",
        load: () => import("@/Pages/Client/SecondBite/Payment"),
    },
    "second-bite.connecting": {
        access: "client",
        load: () => import("@/Pages/Client/SecondBite/YesConnectMe"),
    },
    "client.postPaymentCoachPick": {
        access: "client",
        load: () => import("@/Pages/Client/PostPaymentCoachPick/SelectCoach"),
    },
    "client.pairingInProgress": {
        access: "client",
        load: () => import("@/Pages/Client/PairingInProgressPage"),
    },
    "client.speedMatch": {
        access: "client",
        load: () => import("@/Pages/Client/SpeedMatch"),
    },
    "resume-coaching": {
        access: "client",
        load: () => import("@/Pages/Client/ResumeCoaching/ResumeCoachingPage"),
    },
    "winback-offer": {
        access: "client",
        load: () => import("@/Pages/Client/ResumeCoaching/ResumeCoachingPage"),
    },
} satisfies Record<string, PageDefinition>;

export type PageName = keyof typeof pageDefinitions;

export type PageMatch = {
    name: PageName;
    access: PageAccess;
    params: Record<string, string>;
};

type CompiledPage = {
    name: PageName;
    access: PageAccess;
    regex: RegExp;
    keys: string[];
};

let compiledPages: CompiledPage[] | null = null;

function isPageName(name: string): name is PageName {
    return name in pageDefinitions;
}

function escapeRegex(text: string) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function compileUri(uri: string, wheres: Record<string, unknown> = {}) {
    const keys: string[] = [];
    const source = uri
        .split("/")
        .filter(Boolean)
        .map((segment) => {
            const parameter = /^\{(\w+)(\?)?\}$/.exec(segment);
            if (!parameter) {
                return `/${escapeRegex(segment)}`;
            }

            const [, key, optional] = parameter;
            keys.push(key);
            const where = wheres[key];
            const constraint = typeof where === "string" ? where : "[^/]+";

            return optional ? `(?:/(${constraint}))?` : `/(${constraint})`;
        })
        .join("");

    return { regex: new RegExp(`^${source}/?$`), keys };
}

type ZiggyRoute = {
    uri: string;
    methods: string[];
    wheres?: Record<string, unknown>;
};

function isZiggyRoute(value: unknown): value is ZiggyRoute {
    return (
        typeof value === "object" &&
        value !== null &&
        "uri" in value &&
        typeof value.uri === "string" &&
        "methods" in value &&
        Array.isArray(value.methods)
    );
}

function compilePages(): CompiledPage[] {
    const compiled: CompiledPage[] = [];

    for (const [name, route] of Object.entries(window.Ziggy.routes)) {
        if (
            !isPageName(name) ||
            !isZiggyRoute(route) ||
            !route.methods.includes("GET")
        ) {
            continue;
        }

        compiled.push({
            name,
            access: pageDefinitions[name].access,
            ...compileUri(route.uri, route.wheres),
        });
    }

    // Static paths win over parameterised ones, e.g. /clients/new over /clients/{client}.
    return compiled.sort((a, b) => a.keys.length - b.keys.length);
}

export function pageAcceptsRedirect(pathname: string): boolean {
    const match = matchPage(pathname);
    if (!match) {
        return false;
    }
    const definition: PageDefinition = pageDefinitions[match.name];

    return definition.acceptsRedirect === true;
}

export function isShownToLoggedOutMobileApp(name: PageName): boolean {
    const definition: PageDefinition = pageDefinitions[name];

    return definition.shownToLoggedOutMobileApp === true;
}

export function matchPage(pathname: string): PageMatch | null {
    compiledPages ??= compilePages();

    for (const page of compiledPages) {
        const values = page.regex.exec(pathname);
        if (!values) {
            continue;
        }

        const params: Record<string, string> = {};
        page.keys.forEach((key, index) => {
            const value = values[index + 1];
            if (value !== undefined) {
                params[key] = decodeURIComponent(value);
            }
        });

        return { name: page.name, access: page.access, params };
    }

    return null;
}

const lazyPages = new Map<PageName, LazyExoticComponent<ComponentType>>();

async function loadWithRetries(
    name: PageName,
    attempt = 1,
): Promise<PageModule> {
    try {
        return await pageDefinitions[name].load();
    } catch (error) {
        Sentry.addBreadcrumb({
            message: "Failed to load bundle for page: " + name,
            level: "warning",
            data: { attempt },
        });
        if (attempt > 3) {
            Sentry.captureException(error);
            window.location.assign(
                "/page-load-error?target=" +
                    encodeURIComponent(
                        window.location.pathname + window.location.search,
                    ),
            );
            throw error;
        }
        await new Promise((resolve) =>
            setTimeout(resolve, 500 * (attempt - 1)),
        );

        return loadWithRetries(name, attempt + 1);
    }
}

export function lazyPage(name: PageName): LazyExoticComponent<ComponentType> {
    const existing = lazyPages.get(name);
    if (existing) {
        return existing;
    }

    const component = lazy(() => loadWithRetries(name));
    lazyPages.set(name, component);

    return component;
}

export function preloadPage(pathname: string): void {
    const match = matchPage(pathname);
    if (match) {
        pageDefinitions[match.name].load().catch(() => {});
    }
}

function isOnSlowOrMeteredConnection(): boolean {
    const connection: unknown = Reflect.get(navigator, "connection");
    if (typeof connection !== "object" || connection === null) {
        return false;
    }

    const saveData: unknown = Reflect.get(connection, "saveData");
    const effectiveType: unknown = Reflect.get(connection, "effectiveType");

    return (
        saveData === true ||
        (typeof effectiveType === "string" && effectiveType.endsWith("2g"))
    );
}

let hasStartedIdlePreload = false;

/*
 * Fetches the logged out pages' chunks one at a time during browser idle
 * periods after load, so marketing navigation is instant. Logged in pages are
 * left out: they are only useful after login and would cost logged out
 * visitors bandwidth. Skips entirely on data-saver or 2G.
 */
export function preloadLoggedOutPagesWhenIdle(): void {
    if (hasStartedIdlePreload) {
        return;
    }
    hasStartedIdlePreload = true;

    if (isOnSlowOrMeteredConnection()) {
        return;
    }

    const scheduleIdle = (callback: () => void): void => {
        if (typeof globalThis.requestIdleCallback === "function") {
            globalThis.requestIdleCallback(callback);
        } else {
            globalThis.setTimeout(callback, 1500);
        }
    };

    const queue = Object.values(pageDefinitions)
        .filter((page) => page.access === "public" || page.access === "guest")
        .map((page) => page.load);

    const loadNext = async (): Promise<void> => {
        const load = queue.shift();
        if (!load) {
            return;
        }
        await load().catch(() => {});
        scheduleIdle(() => void loadNext());
    };

    const start = (): void => scheduleIdle(() => void loadNext());
    if (document.readyState === "complete") {
        start();
    } else {
        window.addEventListener("load", start, { once: true });
    }
}
